import { Injectable, Inject } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import {
  from, switchMap, take,
} from 'rxjs';

import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { isAssignedOpcVariantKey, LandingPageHeaderParam, OPC_EXPERIMENT_TEST_VARIANT } from '@sondermind/constants';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { Logger } from '@sondermind/datadog-logging';
import { IrisModalService, IrisModalSize } from '@sondermind/iris/modal';
import { SegmentService } from '@sondermind/segment';
import { USStateOptions } from '@sondermind/utilities/states-list';

import { QuickCheckoutComponent } from '../components/quick-checkout.component';
import { RequestSessionExitClickTarget } from '../models/enums/request-session-exits.enum';
import { IQuickCheckoutModalConfigData } from '../models/quick-checkout-modal-config.model';
import { IRequestASessionClick } from '../models/request-a-session-click.interface';
import { IRequestSessionParams } from '../models/request-session-params.interface';

@Injectable({ providedIn: 'root' })
export class RequestASessionService {
  constructor(
    private irisModalService: IrisModalService,
    private segmentService: SegmentService<SondermindEvents>,
    private router: Router,
    private logger: Logger,
    @Inject(FEATURE_FLAG_SERVICE) private featureFlagService: IFeatureFlagService
  ) {}

  onRequestSessionButtonClicked(click: IRequestASessionClick): void {
    const { requestedSessionPreferences } = click;

    let sessionRequestDetails = null;
    if (!!requestedSessionPreferences?.startTime && !!requestedSessionPreferences?.endTime) {
      const timeDiffInSeconds = Math.floor(
        (requestedSessionPreferences.startTime.getTime() - new Date().getTime()) / 1000
      );
      const hoursFromRequestSubmission = Math.floor(timeDiffInSeconds / 3600);
      sessionRequestDetails = {
        startTime: requestedSessionPreferences.startTime.toLocaleString('en-US', { timeZoneName: 'short' }),
        endTime: requestedSessionPreferences.endTime.toLocaleString('en-US', { timeZoneName: 'short' }),
        sessionType: requestedSessionPreferences.sessionType ?? '',
        hoursFromRequestSubmission
      };
    }

    const clickData = {
      accepting: click.profile.accepting_clients,
      personaProviderId: click.profile.id,
      sessionRequestDetails,
    };

    from(this.segmentService.track<SondermindEvent.RequestASessionClicked>(
      SondermindEvent.RequestASessionClicked,
      clickData
    )).pipe(take(1)).subscribe();

    if (click.isInternal) {
      this.openRequestASessionModal(click, { skipOpcExperiment: true });
      return;
    }

    // MER-5286: a click carrying no slot — "Connect without selecting a time", the header and
    // connect-card CTAs, the mobile drawer fallback, and a `requestSession=true` deep link with
    // no startTime — always gets QuickCheckout and sits outside the experiment population, so
    // exposure must not fire for it. Short-circuit before evaluating the flag: OPC would open on
    // its slot picker, re-asking for the time the client just declined to choose.
    if (!click.requestedSessionPreferences?.startTime) {
      this.openRequestASessionModal(click, { skipOpcExperiment: true });
      return;
    }

    // MER-4944: retest routing. Only users Amplitude actually assigned to a variant
    // count as experiment participants — `opc` (test arm) → OPC page, `control` → QC
    // modal. Every other outcome (null / off / timeout / unknown key) means Amplitude
    // never assigned this user, so they're outside the experiment population and must
    // NOT fire exposure or Started; treat like `isInternal` and open QC with
    // skipOpcExperiment: true. Exposure is fired inside each variant component's
    // ngOnInit (ITT-preserving), gated on !skipOpcExperiment.
    //
    // Timeout defense: 1s timeout on variant evaluation. If it doesn't resolve,
    // treat as unassigned and open QC modal with skipOpcExperiment: true.
    this.featureFlagService.evaluate$<string>(FeatureFlagKey.ONE_PAGE_CHECKOUT_EXPERIMENT_V3, 'off')
      .pipe(take(1))
      .subscribe((variant) => {
        this.logger.info(
          'One-page checkout: feature flag evaluation',
          {
            personaProviderId: click.profile.id,
            variantKey: variant?.key ?? 'not populated',
            variantPayload: variant?.payload ?? 'not populated',
          }
        );

        if (variant?.key === OPC_EXPERIMENT_TEST_VARIANT) {
          if (!click.shareCode) {
            // Defensive: request-a-session click paths (PSL) always carry a shareCode.
            // Reaching this means a caller built a click without one — surfacing it
            // loudly beats silently reclassifying an OPC-arm user as control and
            // poisoning the retest data.
            this.logger.error(
              'One-page checkout: request-a-session click resolved to OPC test variant with no shareCode; aborting',
              { personaProviderId: click.profile.id },
            );
            return;
          }

          const queryParams: { [key: string]: string } = {};
          if (click.requestedSessionPreferences?.startTime) {
            queryParams[LandingPageHeaderParam.SLOT_START_TIME] =
              click.requestedSessionPreferences.startTime.toISOString();
          }
          if (click.requestedSessionPreferences?.endTime) {
            queryParams[LandingPageHeaderParam.SLOT_END_TIME] =
              click.requestedSessionPreferences.endTime.toISOString();
          }
          if (click.requestedSessionPreferences?.sessionType) {
            queryParams[LandingPageHeaderParam.SESSION_TYPE] = click.requestedSessionPreferences.sessionType;
          }

          // Carry the directory's filters through so OPC can prefill, matching what the
          // quick-checkout modal already receives. Note `state` leaves here as an
          // abbreviation, whereas the directory sends it to PSL as a label.
          const stateAbbrev = USStateOptions.find(
            (state) => state.label === click.queryParams.state
          )?.value || click.profile.practice_state?.abbrev;

          if (stateAbbrev) {
            queryParams[LandingPageHeaderParam.STATE] = stateAbbrev;
          }
          if (click.queryParams.paymentPref) {
            queryParams[LandingPageHeaderParam.PAYMENT_PREF] = click.queryParams.paymentPref;
          }
          if (click.queryParams.pncId) {
            queryParams[LandingPageHeaderParam.PNC_ID] = click.queryParams.pncId;
          }

          this.router.navigate([click.shareCode, 'one-page-checkout'], {
            queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined
          });
          return;
        }

        this.openRequestASessionModal(click, { skipOpcExperiment: !isAssignedOpcVariantKey(variant?.key) });
      });
  }

  private openRequestASessionModal(
    click: IRequestASessionClick,
    options: { skipOpcExperiment?: boolean; } = {},
  ): void {
    const queryParamStateAbbrev = USStateOptions.find((state) => state.label === click.queryParams.state)?.value;
    // Filter out possible duplicates in provider_network_companies since the list includes all network states
    const acceptedInsuranceIds = [...new Set(click.profile.provider_network_companies.map((pnc) => pnc.id))];

    const quickCheckoutInputData: IQuickCheckoutModalConfigData = {
      initialState: queryParamStateAbbrev || click.profile.practice_state.abbrev,
      initialPaymentPref: click.queryParams.paymentPref ?? '',
      initialPncId: click.queryParams.pncId ?? '',
      referringUrl: click.queryParams.referringUrl,
      provider: {
        acceptedInsuranceIds,
        credentialedStates: click.profile.credentialed_states.map((state) => state.name),
        id: click.profile.id,
        firstName: click.profile.display_first_name,
        timeZone: click.profile.time_zone,
        primaryAddress: click.providerPrimaryAddress,
        sessionTypesOffered: click.profile.session_types_offered,
        educationLevel: click.profile.education_level
      },
      requestedSessionPreferences: click.requestedSessionPreferences,
      ...options.skipOpcExperiment ? { skipOpcExperiment: true } : {},
    };

    const irisModalRef = this.irisModalService.open<QuickCheckoutComponent, IQuickCheckoutModalConfigData, string>(
      QuickCheckoutComponent,
      {
        size: IrisModalSize.MD,
        data: quickCheckoutInputData,
        // Marker class consumed by quick-checkout.component.scss to size the modal-chrome
        // with dvh so action buttons stay reachable on mobile (MER-4857).
        additionalPanelClasses: ['quick-checkout-overlay'],
      }
    );

    irisModalRef.afterClosed$().pipe(
      take(1),
      switchMap(() => from(this.segmentService.track<SondermindEvent.QuickCheckoutFormExited>(
        SondermindEvent.QuickCheckoutFormExited,
        { exitedBy: RequestSessionExitClickTarget.EXIT_X_BUTTON })
      )),
    ).subscribe();
  }

  getNameFromParams(routeParams: ParamMap): string {
    return routeParams.get(LandingPageHeaderParam.NAME) ?? '';
  }

  getShareCodeFromParams(routeParams: ParamMap): string {
    return routeParams.get(LandingPageHeaderParam.SHARE_CODE) ?? '';
  }

  parseQueryParams(queryParams: ParamMap): IRequestSessionParams {
    const incomingState: string | null = queryParams.get(LandingPageHeaderParam.STATE);
    const incomingPaymentPreference: string | null = queryParams.get(LandingPageHeaderParam.PAYMENT_PREF);
    const incomingInsuranceTag: string | null = queryParams.get(LandingPageHeaderParam.PAYMENT_TAG);
    const incomingPncId: string | null = queryParams.get(LandingPageHeaderParam.PNC_ID);

    let requestSessionQueryParams: IRequestSessionParams = {
      anonUserId: queryParams.get(LandingPageHeaderParam.ANON_USER_ID) ?? '',
      referringUrl: queryParams.get(LandingPageHeaderParam.REFERRING_URL)
        ?? encodeURIComponent(window.location.href),
    };
    if (incomingState) {
      requestSessionQueryParams = { ...requestSessionQueryParams, state: incomingState };
    }
    if (incomingPaymentPreference) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentPref: incomingPaymentPreference };
    }
    if (incomingInsuranceTag) {
      requestSessionQueryParams = { ...requestSessionQueryParams, paymentTag: incomingInsuranceTag };
    }
    if (incomingPncId) {
      requestSessionQueryParams = { ...requestSessionQueryParams, pncId: incomingPncId };
    }

    return requestSessionQueryParams;
  }
}
